import { useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Conversation } from '@/types/chat'
import { ParticipantRole } from '@/types/groupChat'
import { Users, MoreVertical, UserPlus, Link as LinkIcon, LogOut } from 'lucide-react'
import ParticipantList from './ParticipantList'
import InviteManager from './InviteManager'
import AddParticipantDialog from './AddParticipantDialog'
import { groupChatService } from '@/services/groupChatService'
import { toast } from 'sonner'
import RoleBadge from './RoleBadge'
import { useTranslation } from 'react-i18next'

interface GroupChatViewProps {
    conversation: Conversation
    currentUserId: string
    children: React.ReactNode // Message area and input
}

/**
 * GroupChatView Component
 * Main component for displaying group chat interface
 * Includes group header, participant list, and action buttons
 */
export default function GroupChatView({
    conversation,
    currentUserId,
    children,
}: GroupChatViewProps) {
    const { t } = useTranslation('common')
    const [showParticipants, setShowParticipants] = useState(false)
    const [showInviteManager, setShowInviteManager] = useState(false)
    const [showAddParticipant, setShowAddParticipant] = useState(false)

    // Get current user's participant info
    const currentParticipant = conversation.conversationParticipants?.find(
        (p) => p.userId === currentUserId && p.status === 'ACTIVE'
    )

    const currentUserRole = currentParticipant?.role || 'MEMBER'

    // Get active participants
    const activeParticipants =
        conversation.conversationParticipants?.filter(
            (p) => p.status === 'ACTIVE'
        ) || []

    // Check permissions
    const canManageParticipants =
        currentUserRole === 'ADMIN' || currentUserRole === 'MODERATOR'
    const canGenerateInvites =
        currentUserRole === 'ADMIN' || currentUserRole === 'MODERATOR'

    // Handle participant actions
    const handleRemoveParticipant = async (userId: string) => {
        try {
            await groupChatService.removeParticipant(conversation.id, userId)
            toast.success(t('chat.groupChatView.success.participantRemoved'))
        } catch (error: any) {
            toast.error(
                error.response?.data?.message || t('chat.groupChatView.errors.removeFailed')
            )
        }
    }

    const handleKickParticipant = async (userId: string) => {
        try {
            await groupChatService.kickParticipant(conversation.id, userId)
            toast.success(t('chat.groupChatView.success.participantKicked'))
        } catch (error: any) {
            toast.error(error.response?.data?.message || t('chat.groupChatView.errors.kickFailed'))
        }
    }

    const handleChangeRole = async (userId: string, newRole: ParticipantRole) => {
        try {
            await groupChatService.updateParticipantRole(conversation.id, userId, {
                role: newRole,
            })
            toast.success(t('chat.groupChatView.success.roleUpdated'))
        } catch (error: any) {
            toast.error(
                error.response?.data?.message || t('chat.groupChatView.errors.updateRoleFailed')
            )
        }
    }

    const handleLeaveGroup = async () => {
        // Check if user is the last admin
        const adminCount = activeParticipants.filter(
            (p) => p.role === 'ADMIN'
        ).length
        const isLastAdmin = currentUserRole === 'ADMIN' && adminCount === 1

        if (isLastAdmin) {
            toast.error(t('chat.groupChatView.errors.cannotLeaveAsLastAdmin'))
            return
        }

        if (
            !confirm(
                t('chat.groupChatView.confirmLeave')
            )
        ) {
            return
        }

        try {
            await groupChatService.leaveGroup(conversation.id)
            toast.success(t('chat.groupChatView.success.leftGroup'))
        } catch (error: any) {
            toast.error(error.response?.data?.message || t('chat.groupChatView.errors.leaveFailed'))
        }
    }

    return (
        <div className="flex flex-col h-full">
            {/* Group header */}
            <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {/* Group avatar */}
                        <Avatar className="h-10 w-10">
                            <AvatarImage
                                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                                    conversation.name
                                )}&size=128&background=random`}
                            />
                            <AvatarFallback>
                                <Users className="h-5 w-5" />
                            </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <h2 className="font-semibold text-lg truncate">
                                    {conversation.name}
                                </h2>
                                {currentParticipant && (
                                    <RoleBadge role={currentUserRole} />
                                )}
                            </div>
                            <p className="text-sm text-gray-500">
                                {activeParticipants.length} {activeParticipants.length !== 1 ? t('chat.groupChatView.participantsPlural') : t('chat.groupChatView.participants')}
                            </p>
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center space-x-2">
                        {/* Group actions menu */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onClick={() => setShowParticipants(true)}
                                >
                                    <Users className="h-4 w-4 mr-2" />
                                    {t('chat.groupChatView.viewParticipants')}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {canManageParticipants && (
                                    <>
                                        <DropdownMenuItem
                                            onClick={() => setShowAddParticipant(true)}
                                        >
                                            <UserPlus className="h-4 w-4 mr-2" />
                                            {t('chat.groupChatView.addParticipant')}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                    </>
                                )}
                                {canGenerateInvites && (
                                    <>
                                        <DropdownMenuItem
                                            onClick={() => setShowInviteManager(true)}
                                        >
                                            <LinkIcon className="h-4 w-4 mr-2" />
                                            {t('chat.groupChatView.manageInvites')}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                    </>
                                )}

                                <DropdownMenuItem onClick={handleLeaveGroup}>
                                    <LogOut className="h-4 w-4 mr-2" />
                                    {t('chat.groupChatView.leaveGroup')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </div>

            {/* Main content area */}
            <div className="flex flex-1 overflow-hidden">
                {/* Messages and input (passed as children) - now takes full width */}
                <div className="flex-1 flex flex-col w-full">{children}</div>
            </div>

            {/* Participants dialog (opens from 3 dots menu) */}
            <Dialog open={showParticipants} onOpenChange={setShowParticipants}>
                <DialogContent className="max-w-2xl max-h-[80vh] p-0 flex flex-col">
                    <DialogHeader className="px-6 pt-6 pb-4 border-b">
                        <DialogTitle>{t('chat.groupChatView.participantsTitle')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-hidden">
                        <ParticipantList
                            participants={conversation.conversationParticipants || []}
                            currentUserId={currentUserId}
                            currentUserRole={currentUserRole}
                            onRemoveParticipant={
                                canManageParticipants ? handleRemoveParticipant : undefined
                            }
                            onKickParticipant={
                                canManageParticipants ? handleKickParticipant : undefined
                            }
                            onChangeRole={
                                currentUserRole === 'ADMIN' ? handleChangeRole : undefined
                            }
                            showInactive={currentUserRole === 'ADMIN'}
                        />
                    </div>
                </DialogContent>
            </Dialog>

            {/* Invite manager dialog */}
            <InviteManager
                open={showInviteManager}
                onOpenChange={setShowInviteManager}
                conversationId={conversation.id}
            />

            {/* Add participant dialog */}
            <AddParticipantDialog
                open={showAddParticipant}
                onOpenChange={setShowAddParticipant}
                conversationId={conversation.id}
            />
        </div>
    )
}
